import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { getPublicSupabaseEnv } from './supabase-env'

const { url: supabaseUrl, anonKey: supabaseAnonKey } = getPublicSupabaseEnv()

// Supabase is also imported by server-rendered routes. Letting Next's patched
// fetch layer cache/dedupe these JSON responses IN DEVELOPMENT can hand the
// PostgREST parser a malformed body when metadata and page rendering request a
// large collection at the same time.
//
// Development only, and that is load-bearing. Applied in production it forced
// every ISR route that queries Supabase from static to dynamic at runtime,
// which Next treats as a hard error: `/sounds/[id]` (revalidate=86400,
// dynamicParams=true) returned 500 for every slug outside
// generateStaticParams, because `getRelatedSounds` runs a `getSounds` query
// during the render. The route owns its ISR policy and the transport must not
// override it.
const supabaseFetch: typeof fetch =
  process.env.NODE_ENV === 'development'
    ? (input, init) => fetch(input, { ...init, cache: 'no-store' })
    : fetch

// Nothing on this site signs in — every query runs as `anon`. Left on the
// defaults, supabase-js still stands up its session machinery in the browser:
// it reads the token back out of localStorage and serialises the refresh behind
// a `navigator.locks` lock named `lock:sb-<ref>-auth-token`. Two tabs, or a
// pageshow from the bfcache, make the second holder steal the lock, and the
// rejection surfaces as `Lock "lock:sb-…-auth-token" was released because
// another request stole it` — thrown out of whatever query happened to be in
// flight, which is how it reached Sentry wrapped as a `getSoundsCount` failure
// on /sounds (TESLA-LOCK-SOUND-6T / -6S). There is no session to persist or
// refresh, so there is nothing for the lock to protect.
const anonOnlyAuth = {
  persistSession: false,
  autoRefreshToken: false,
  detectSessionInUrl: false,
} as const

// Nullable client for use in API routes that need explicit null checks (CI/build safety)
export const supabaseClient = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: anonOnlyAuth,
      global: { fetch: supabaseFetch },
    })
  : null

// Non-null export for client-side code that only runs in the browser
// where env vars are always present (hooks, realtime, components)
export const supabase = supabaseClient as SupabaseClient
